import * as React from 'react';
import { format, parse, isValid, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import Calendar from '../Calendar';
import Popover from '../Popover';
import { useTheme } from '../../../../ui/src/contextProviders';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export type DatePickerWithRangeProps = {
  id: string;
  /** YYYYMMDD (19921022) */
  startDate: string;
  /** YYYYMMDD (19921022) */
  endDate: string;
  /** Callback that received { startDate: string(YYYYMMDD), endDate: string(YYYYMMDD)} */
  onChange: (value: { startDate: string; endDate: string }) => void;
  startPlaceholder?: string;
  endPlaceholder?: string;
  dateFormat?: string;
  shouldReset?: boolean;
};

type PresetRange = {
  label: string;
  getRange: () => { start: Date; end: Date };
};

export function DatePickerWithRange({
  className,
  id,
  startDate,
  endDate,
  onChange,
  startPlaceholder,
  endPlaceholder,
  dateFormat,
  shouldReset,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & DatePickerWithRangeProps) {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { isDarkMode } = useTheme();
  const formatStr = dateFormat || 'dd-MM-yyyy';
  const [start, setStart] = React.useState<string>(
    startDate ? format(parse(startDate, 'ddMMyyyy', new Date()), formatStr) : ''
  );
  const [end, setEnd] = React.useState<string>(
    endDate ? format(parse(endDate, 'ddMMyyyy', new Date()), formatStr) : ''
  );
  const [openEnd, setOpenEnd] = React.useState(false);
  const [showPresets, setShowPresets] = React.useState(false);

  const presetRanges: PresetRange[] = [
    {
      label: 'Today',
      getRange: () => {
        const today = new Date();
        return { start: today, end: today };
      }
    },
    {
      label: 'This Week',
      getRange: () => {
        const today = new Date();
        return { start: startOfWeek(today), end: endOfWeek(today) };
      }
    },
    {
      label: 'This Month',
      getRange: () => {
        const today = new Date();
        return { start: startOfMonth(today), end: endOfMonth(today) };
      }
    },
    {
      label: 'This Year',
      getRange: () => {
        const today = new Date();
        return { start: startOfYear(today), end: endOfYear(today) };
      }
    }
  ];

  const handlePresetSelect = (preset: PresetRange) => {
    const { start: startDate, end: endDate } = preset.getRange();
    const formattedStart = format(startDate, formatStr);
    const formattedEnd = format(endDate, formatStr);

    setStart(formattedStart);
    setEnd(formattedEnd);
    setShowPresets(false);

    onChange({
      startDate: format(startDate, 'ddMMyyyy'),
      endDate: format(endDate, 'ddMMyyyy'),
    });
  };

  const handleStartSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const formattedStart = format(selectedDate, formatStr);
      setStart(formattedStart);
      setEnd('');
      setOpenEnd(true);
      onChange({
        startDate: format(selectedDate, 'ddMMyyyy'),
        endDate: '',
      });
    }
  };

  const handleEndSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const formattedEnd = format(selectedDate, formatStr);
      setEnd(formattedEnd);
      setOpenEnd(false);
      onChange({
        startDate: start.replace(/-/g, ''),
        endDate: format(selectedDate, 'ddMMyyyy'),
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
    const value = e.target.value;
    const parsedDate = parse(value, formatStr, new Date());
    if (type === 'start') {
      setStart(value);
      if (isValid(parsedDate)) handleStartSelect(parsedDate);
    } else {
      setEnd(value);
      if (isValid(parsedDate)) handleEndSelect(parsedDate);
    }
  };

  useEffect(() => {
    setStart(startDate ? format(parse(startDate, 'ddMMyyyy', new Date()), formatStr) : '');
    setEnd(endDate ? format(parse(endDate, 'ddMMyyyy', new Date()), formatStr) : '');
  }, [startDate, endDate]);

  useEffect(() => {
    if (shouldReset) {
      setStart('');
      setEnd('');
    }
  }, [shouldReset]);

  return (
    <div className={cn('CalenderGroup flex gap-2', className)}>
      {isHomePage && (
        <Popover.Popover open={showPresets} onOpenChange={setShowPresets}>
          <Popover.PopoverTrigger asChild>
            <button
              className={cn(
                'flex h-[32px] items-center justify-between rounded border px-3 text-sm',
                !isDarkMode ? 'bg-black text-white' : 'bg-white text-black'
              )}
            >
              <span style={{ color: isDarkMode ? 'white' : 'black' }}>Quick Select</span>
              <ChevronDown className="ml-2 h-6 w-6" style={{ color: isDarkMode ? 'white' : 'black' }} />
            </button>
          </Popover.PopoverTrigger>
          <Popover.PopoverContent className="w-auto p-0" align="start">
            <div className="flex flex-col">
              {presetRanges.map((preset) => (
                <button
                  key={preset.label}
                  className="px-4 py-2 text-left text-sm hover:bg-gray-100"
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </Popover.PopoverContent>
        </Popover.Popover>
      )}

      <Popover.Popover>
        <Popover.PopoverTrigger asChild>
          <div className="relative w-full">
            <CalendarIcon className={cn("absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 transform ",
              isDarkMode ? "text-blackDark" : 'text-white')}
              style={{ color: "#ab9a9a" }}
              />
            <input
              id={`${id}-start`}
              type="text"
              placeholder={startPlaceholder || 'Start Date'}
              autoComplete="off"
              value={start}
              onChange={e => handleInputChange(e, 'start')}
              className={cn(
                '!border-inputfield-main focus:border-inputfield-focus h-[32px] w-full justify-start rounded border py-[6.5px] pl-[6.5px] pr-[6.5px] text-left text-sm font-normal',
                !start && 'text-muted-foreground',

              )}
              data-cy="input-date-range-start"
              style={{ minWidth: '110px' }}
            />
          </div>
        </Popover.PopoverTrigger>
        <Popover.PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="single"
            defaultMonth={start ? parse(start, formatStr, new Date()) : new Date()}
            selected={start ? parse(start, formatStr, new Date()) : undefined}
            onSelect={handleStartSelect}
            numberOfMonths={1}
          />
        </Popover.PopoverContent>
      </Popover.Popover>

      <Popover.Popover open={openEnd} onOpenChange={setOpenEnd}>
        <Popover.PopoverTrigger asChild>
          <div className="relative w-full">
            <CalendarIcon className={cn("absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 transform",
              isDarkMode ? "text-blackDark" : "text-white")}
              style={{ color: "#ab9a9a" }}
              />
            <input
              id={`${id}-end`}
              type="text"
              placeholder={endPlaceholder || 'End Date'}
              autoComplete="off"
              value={end}
              onChange={e => handleInputChange(e, 'end')}
              className={cn(
                '!border-inputfield-main focus:border-inputfield-focus h-full w-full justify-start rounded border py-[6.5px] pl-[6.5px] pr-[6.5px] text-left text-sm font-normal',
                !end && 'text-muted-foreground',
              )}
              data-cy="input-date-range-end"
              style={{ minWidth: '110px' }}
            />
          </div>
        </Popover.PopoverTrigger>
        <Popover.PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="single"
            defaultMonth={start ? parse(start, formatStr, new Date()) : new Date()}
            selected={end ? parse(end, formatStr, new Date()) : undefined}
            onSelect={handleEndSelect}
            numberOfMonths={1}
            disabled={(date) => {
              const parsedStart = start ? parse(start, formatStr, new Date()) : null;
              return parsedStart && date < parsedStart;
            }}
          />
        </Popover.PopoverContent>
      </Popover.Popover>
    </div>
  );
}
